/**
 * Hex grid mathematics — pointy-top hexes, axial coordinates.
 *
 * Two coordinate systems, deliberately:
 *
 *   AXIAL (q, r)   — used by all *game logic*. Distance, neighbours, rings and
 *                    pathfinding are simple and symmetric here. The third cube
 *                    coordinate is implied: s = -q - r.
 *
 *   OFFSET (col,row) — used only for *storage*. The map is a rectangle of tiles in
 *                    flat typed arrays, so we need a rectangular addressing scheme.
 *                    We use "odd-r": odd-numbered rows are shifted half a hex right.
 *
 * Everything converts at the boundary. Game code should never see col/row; storage
 * code should never see q/r. Mixing them is the classic source of off-by-one-row bugs.
 */

export interface Hex {
  readonly q: number;
  readonly r: number;
}

export interface Offset {
  readonly col: number;
  readonly row: number;
}

export function hex(q: number, r: number): Hex {
  return { q, r };
}

/** The six neighbour directions in axial space, clockwise from east. */
export const HEX_DIRECTIONS: readonly Hex[] = [
  { q: 1, r: 0 }, // east
  { q: 1, r: -1 }, // north-east
  { q: 0, r: -1 }, // north-west
  { q: -1, r: 0 }, // west
  { q: -1, r: 1 }, // south-west
  { q: 0, r: 1 }, // south-east
];

/** Human-readable direction names, indexed to match HEX_DIRECTIONS. */
export const HEX_DIRECTION_NAMES = ['E', 'NE', 'NW', 'W', 'SW', 'SE'] as const;
export type HexDirection = 0 | 1 | 2 | 3 | 4 | 5;

export function hexEquals(a: Hex, b: Hex): boolean {
  return a.q === b.q && a.r === b.r;
}

export function hexAdd(a: Hex, b: Hex): Hex {
  return { q: a.q + b.q, r: a.r + b.r };
}

export function hexSubtract(a: Hex, b: Hex): Hex {
  return { q: a.q - b.q, r: a.r - b.r };
}

export function hexScale(a: Hex, k: number): Hex {
  return { q: a.q * k, r: a.r * k };
}

/** The neighbour in direction `dir` (0-5, see HEX_DIRECTIONS). */
export function hexNeighbor(h: Hex, dir: HexDirection): Hex {
  return hexAdd(h, HEX_DIRECTIONS[dir]!);
}

/** All six neighbours, in direction order. Allocates — do not call in a hot inner loop. */
export function hexNeighbors(h: Hex): Hex[] {
  return HEX_DIRECTIONS.map((d) => hexAdd(h, d));
}

/**
 * Distance in hexes. On a hex grid this is honest: every neighbour is exactly 1
 * away, with none of the "is a diagonal 1 or 1.5?" arbitrariness of a square grid.
 */
export function hexDistance(a: Hex, b: Hex): number {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return (Math.abs(dq) + Math.abs(dq + dr) + Math.abs(dr)) / 2;
}

/** The `radius`-th ring around `center`: exactly 6 × radius hexes, in order. */
export function hexRing(center: Hex, radius: number): Hex[] {
  if (radius < 0) throw new Error(`hexRing radius must be >= 0, got ${radius}`);
  if (radius === 0) return [{ q: center.q, r: center.r }];

  const results: Hex[] = [];
  // Start at the westmost hex of the ring, then walk each of the six sides.
  let current = hexAdd(center, hexScale(HEX_DIRECTIONS[4]!, radius));
  for (let side = 0; side < 6; side++) {
    for (let step = 0; step < radius; step++) {
      results.push(current);
      current = hexNeighbor(current, side as HexDirection);
    }
  }
  return results;
}

/** Every hex within `radius` of `center`, centre first, then ring by ring. */
export function hexSpiral(center: Hex, radius: number): Hex[] {
  const results: Hex[] = [];
  for (let k = 0; k <= radius; k++) results.push(...hexRing(center, k));
  return results;
}

/** Straight line between two hexes, inclusive of both ends. Used for bombardment range and borders. */
export function hexLine(a: Hex, b: Hex): Hex[] {
  const n = hexDistance(a, b);
  if (n === 0) return [{ q: a.q, r: a.r }];
  const results: Hex[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    results.push(hexRound(a.q + (b.q - a.q) * t, a.r + (b.r - a.r) * t));
  }
  return results;
}

/** Round fractional axial coordinates to the nearest hex, via cube rounding. */
export function hexRound(qf: number, rf: number): Hex {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);

  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);

  // Discard whichever coordinate moved furthest, keeping q + r + s === 0.
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;

  return { q, r };
}

// ─── axial ↔ odd-r offset ──────────────────────────────────────────────────────

export function axialToOffset(h: Hex): Offset {
  const col = h.q + (h.r - (h.r & 1)) / 2;
  return { col, row: h.r };
}

export function offsetToAxial(col: number, row: number): Hex {
  const q = col - (row - (row & 1)) / 2;
  return { q, r: row };
}

// ─── screen-space conversion (used by the renderer, kept here with the maths) ──

/**
 * Centre of a hex in pixels, for a pointy-top hex whose *circumradius* is `size`
 * (i.e. size is the distance from centre to corner).
 */
export function hexToPixel(h: Hex, size: number): { x: number; y: number } {
  return {
    x: size * Math.sqrt(3) * (h.q + h.r / 2),
    y: size * 1.5 * h.r,
  };
}

/** The hex containing a pixel position. Inverse of hexToPixel. */
export function pixelToHex(x: number, y: number, size: number): Hex {
  const q = ((Math.sqrt(3) / 3) * x - (1 / 3) * y) / size;
  const r = ((2 / 3) * y) / size;
  return hexRound(q, r);
}

/** The six corner offsets of a pointy-top hex, relative to its centre. */
export function hexCorners(size: number): Array<{ x: number; y: number }> {
  const corners: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i - 30);
    corners.push({ x: size * Math.cos(angle), y: size * Math.sin(angle) });
  }
  return corners;
}
